import {
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Center,
  Icon,
  Skeleton,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { IoNotificationsOutline, IoRefresh } from "react-icons/io5";
import { ErrorBoundary } from "react-error-boundary";
import { useAtom, useAtomValue } from "jotai";
import {
  usePrefetchInfiniteQuery,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query";
import { accessTokenInfoAtom, footerIndexAtom } from "../store";
import {
  notificationsQueryOptions,
  reminderHistsQueryOptions,
} from "../stores/notificationStore";
import { BottomNavLayout } from "../components/templates/BottomNavLayout";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { LoadErrorBand } from "../components/molecules/LoadErrorBand";
import { NotificationRow } from "../components/molecules/Notification/NotificationRow";
import { NotificationCategoryChips } from "../components/molecules/Notification/NotificationCategoryChips";
import {
  NotificationCategory,
  toNotificationList,
} from "../components/molecules/Notification/notificationTypes";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";
import { useToastMessage } from "../hooks/useToastMessage";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";

type ListQueries = {
  alert: ReturnType<typeof notificationsQueryOptions>;
  reminder: ReturnType<typeof reminderHistsQueryOptions>;
};

const ALL_CATEGORIES_ON: Record<NotificationCategory, boolean> = {
  low: true,
  high: true,
  trend: true,
  reminder: true,
};

/*
 * 通知一覧画面。本人に届いた通知とリマインダーを新しい順に並べる
 */
export function Notifications(): ReactElement {
  const t = useModifiedTranslation();
  const [, setFooterIndex] = useAtom(footerIndexAtom);
  const tokenInfo = useAtomValue(accessTokenInfoAtom);
  // 開くたびに最初から読む。「もう一度」も開き直したときと同じく両方を最初から読む
  const [openedAt, setOpenedAt] = useState(Date.now);
  const queries: ListQueries = {
    alert: notificationsQueryOptions(tokenInfo, openedAt),
    reminder: reminderHistsQueryOptions(tokenInfo, openedAt),
  };
  // 一覧は2つを順に読み、1つ目を取り終えるまで2つ目を取りに行かないので、ここで両方を同時に取り始める
  usePrefetchInfiniteQuery(queries.alert);
  usePrefetchInfiniteQuery(queries.reminder);

  useEffect(() => {
    setFooterIndex(4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BottomNavLayout>
      <BasicPageLayout
        title={t("Notification.title")}
        titleProps={{ fontSize: "2xl" }}
      >
        <ErrorBoundary
          fallbackRender={({ error, resetErrorBoundary }) => (
            // どちらかの履歴を読み込めなかったとき。「もう一度」で両方を取り直す
            <LoadErrorBand
              error={error}
              message={t("Notification.error.message")}
              onRetry={() => resetErrorBoundary()}
              testId="notification-error"
              retryTestId="notification-retry"
            />
          )}
          onReset={() => setOpenedAt(Date.now())}
        >
          <Suspense fallback={<NotificationSkeleton />}>
            <NotificationList queries={queries} />
          </Suspense>
        </ErrorBoundary>
      </BasicPageLayout>
    </BottomNavLayout>
  );
}

// 読み込み中はチップと行の形の灰色の箱を出す。チップは実物と同じく左右両端にそろえ、
// 行は3本の棒（短い・長い・中くらい）を一覧と同じ区切り線で分ける
function NotificationSkeleton(): ReactElement {
  return (
    <Box data-testid="notification-loading">
      <Box display="flex" justifyContent="space-between" gap={2} py={3}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} h={8} w={20} rounded="full" />
        ))}
      </Box>
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          py={3}
          borderBottomWidth="1px"
          borderColor="chakra-border-color"
        >
          <Skeleton h="13px" w="40%" rounded="full" mb={3} />
          <Skeleton h="10px" w="85%" rounded="full" mb={2} />
          <Skeleton h="10px" w="60%" rounded="full" />
        </Box>
      ))}
    </Box>
  );
}

function EmptyMessage(
  props: Readonly<{ title: string; description: string; testId: string }>,
): ReactElement {
  const { title, description, testId } = props;
  return (
    <Center flexDirection="column" py={10} px={4} data-testid={testId}>
      {/* theme_lv3 はダークテーマでも明るいままなので、ダークでは地の色に半透明の白を重ねる */}
      <Center
        boxSize={14}
        rounded="full"
        bg="primary.theme_lv3"
        _dark={{ bg: "whiteAlpha.100" }}
        mb={4}
      >
        <Icon
          as={IoNotificationsOutline}
          boxSize={5}
          color="text.main_text_lv1"
        />
      </Center>
      <Text fontWeight="bold" textAlign="center">
        {title}
      </Text>
      <Text
        fontSize="13px"
        fontWeight="normal"
        color="text.sub_text"
        textAlign="center"
        mt={2}
      >
        {description}
      </Text>
    </Center>
  );
}

function NotificationList(
  props: Readonly<{ queries: ListQueries }>,
): ReactElement {
  const t = useModifiedTranslation();
  const toastMessage = useToastMessage();
  const notificationsQuery = useSuspenseInfiniteQuery(props.queries.alert);
  const remindersQuery = useSuspenseInfiniteQuery(props.queries.reminder);
  // 絞り込みは端末の中だけで行い、開き直すと全部オンに戻る
  const [enabled, setEnabled] = useState(ALL_CATEGORIES_ON);
  // 開いている行。開けるのは1行だけ。絞り込みで隠れても覚えておき、また出たときは開いたまま出す
  const [openKey, setOpenKey] = useState<string | null>(null);
  const { data: alertPages, hasNextPage: hasNextAlertPage } =
    notificationsQuery;
  const { data: reminderPages, hasNextPage: hasNextReminderPage } =
    remindersQuery;
  const { rows, next, hasAny } = useMemo(
    () =>
      toNotificationList(
        { data: alertPages, hasNextPage: hasNextAlertPage },
        { data: reminderPages, hasNextPage: hasNextReminderPage },
        enabled,
      ),
    [alertPages, hasNextAlertPage, reminderPages, hasNextReminderPage, enabled],
  );
  // 番兵・スピナー・「もう一度」は、next の側のクエリの状態で出す。next が null なら出さない
  const nextQuery =
    next === null
      ? null
      : { alert: notificationsQuery, reminder: remindersQuery }[next];
  const fetchNextPage = nextQuery?.fetchNextPage;
  const isFetchingNextPage = nextQuery?.isFetchingNextPage ?? false;
  const isFetchNextPageError = nextQuery?.isFetchNextPageError ?? false;

  // 次ページの失敗は一覧を消さずトーストだけ出す（fetchNextPage は失敗しても throw しない）。
  // 失敗後は番兵を外して自動取得を止め、「もう一度」で再開する
  const loadMore = useCallback(async (): Promise<void> => {
    if (!fetchNextPage) return;
    const result = await fetchNextPage();
    if (result.isFetchNextPageError) toastMessage(result.error);
  }, [fetchNextPage, toastMessage]);
  const canAutoLoad = next !== null && !isFetchNextPageError;

  // 絞り込みで0件でも番兵は残し、隠れている種別が次ページにあり得るので読み続ける
  const sentinelRef = useInfiniteScroll({
    hasNextPage: canAutoLoad,
    isFetchingNextPage,
    onLoadMore: loadMore,
  });

  const listFooter = (): ReactElement | null => {
    if (isFetchingNextPage) return <LoadingMore />;
    if (isFetchNextPageError) return <LoadMoreRetry onRetry={loadMore} />;
    if (canAutoLoad) return <Box ref={sentinelRef} h="1px" />;
    return null;
  };

  // 先頭ページが一覧に出さない種別だけのこともあるので、続きがある間は空状態にせず読み続ける
  if (!hasAny && next !== null) {
    return <>{listFooter()}</>;
  }

  // 通知もリマインダーも1件もないときはチップも出さない
  if (!hasAny) {
    return (
      <EmptyMessage
        title={t("Notification.empty.title")}
        description={t("Notification.empty.description")}
        testId="notification-empty"
      />
    );
  }

  // 続きを待っている間は、選んだ種別が0件とはまだ決まらない
  const isFilteredEmpty = rows.length === 0 && next === null;

  return (
    <>
      <NotificationCategoryChips
        enabled={enabled}
        onToggle={(category) =>
          setEnabled((prev) => ({ ...prev, [category]: !prev[category] }))
        }
      />
      {isFilteredEmpty ? (
        <EmptyMessage
          title={t("Notification.filtered.title")}
          description={t("Notification.filtered.description")}
          testId="notification-filtered-empty"
        />
      ) : (
        <Box>
          {rows.map((item, index) => (
            <NotificationRow
              key={item.key}
              item={item}
              isOpen={item.key === openKey}
              onToggle={() =>
                setOpenKey((prev) => (prev === item.key ? null : item.key))
              }
              testId={`notification-row-${index}`}
            />
          ))}
        </Box>
      )}
      {listFooter()}
    </>
  );
}

function LoadingMore(): ReactElement {
  return (
    <Center py={4} data-testid="notification-loading-more">
      <Spinner size="sm" color="common_text.gray" />
    </Center>
  );
}

function LoadMoreRetry(props: Readonly<{ onRetry: () => void }>): ReactElement {
  const t = useModifiedTranslation();
  return (
    <Center
      flexDirection="column"
      py={4}
      data-testid="notification-load-more-retry"
    >
      <Text fontSize="sm" color="common_text.gray">
        {t("Notification.loadMore.error")}
      </Text>
      <Button
        size="sm"
        variant="link"
        leftIcon={<IoRefresh />}
        mt={2}
        onClick={props.onRetry}
        data-testid="notification-load-more-retry-button"
      >
        {t("Notification.error.retry")}
      </Button>
    </Center>
  );
}
